import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, formatErr } from "@/lib/api";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { TableCard, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import { Wallet, Boxes, TrendingUp, Plus, Trash2, Landmark, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";

const TYPES = {
  investment: { label: "Investment (permanent)", tone: "text-agri-forest", sign: "+" },
  topup: { label: "Temporary top-up", tone: "text-agri-amber-dark", sign: "+" },
  withdrawal: { label: "Withdrawal (cash out)", tone: "text-agri-danger", sign: "−" },
};
const EMPTY = { type: "topup", amount: 0, note: "", date: "" };

function Kpi({ label, value, icon: Icon, tone = "forest", hint }) {
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-cap">{label}</div>
          <div className={`mt-2 text-2xl font-bold tabular-nums ${tone === "amber" ? "text-agri-amber-dark" : tone === "danger" ? "text-agri-danger" : "text-agri-forest"}`}>{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tone === "amber" ? "bg-agri-amber/10 text-agri-amber" : tone === "danger" ? "bg-red-50 text-agri-danger" : "bg-agri-forest/10 text-agri-forest"}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function Financials() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const fe = useFormErrors();

  const { data: fp } = useQuery({ queryKey: ["financial-position"], queryFn: () => api.get("/reports/financial-position").then((r) => r.data), refetchInterval: 30000 });
  const { data: moves = [] } = useQuery({ queryKey: ["cash-movements"], queryFn: () => api.get("/cash-movements").then((r) => r.data) });
  const { data: daily = [] } = useQuery({ queryKey: ["cash-daily"], queryFn: () => api.get("/reports/cash-daily", { params: { days: 90 } }).then((r) => r.data) });
  const [detailDate, setDetailDate] = useState(null);
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["cash-day-detail", detailDate],
    queryFn: () => api.get("/reports/cash-day-detail", { params: { date: detailDate } }).then((r) => r.data),
    enabled: !!detailDate,
  });

  const refresh = () => {
    ["financial-position", "cash-movements", "cash-daily", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const save = useMutation({
    mutationFn: () => api.post("/cash-movements", { ...form, amount: parseFloat(form.amount), date: form.date || null }),
    onSuccess: () => { toast.success("Recorded"); refresh(); setOpen(false); setForm(EMPTY); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/cash-movements/${id}`),
    onSuccess: () => { toast.success("Deleted"); refresh(); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const b = fp?.breakdown;

  return (
    <div className="space-y-6" data-testid="financials-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="label-cap">Finance</div><h1 className="font-heading text-3xl mt-1">Financial Position</h1></div>
        <button onClick={() => { fe.clear(); setForm(EMPTY); setOpen(true); }} className={btnAccent + " flex items-center gap-2"}><Plus size={16} /> Add Cash / Capital</button>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Kpi label="Cash in Hand" value={fmtMoney(fp?.cash_in_hand || 0)} icon={Wallet} tone={(fp?.cash_in_hand || 0) >= 0 ? "forest" : "danger"} />
        <Kpi label="Stock Value (at cost)" value={fmtMoney(fp?.stock_value_cost || 0)} icon={Boxes} hint={`Retail ${fmtMoney(fp?.stock_value_retail || 0)}`} />
        <Kpi label="Receivable − Payable" value={fmtMoney((fp?.receivable || 0) - (fp?.payable || 0))} icon={ArrowDownCircle} tone="amber" hint={`Owed to you ${fmtMoney(fp?.receivable || 0)} · you owe ${fmtMoney(fp?.payable || 0)}`} />
        <Kpi label="Business Worth" value={fmtMoney(fp?.business_worth || 0)} icon={TrendingUp} hint="Cash + Stock + Receivable − Payable" />
      </div>

      {/* Capital */}
      <div className="card-soft p-6">
        <div className="flex items-center gap-2 mb-4"><Landmark size={18} className="text-agri-forest" /><h3 className="font-heading text-xl">Owner Capital</h3></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div><div className="label-cap">Permanent investment</div><div className="mt-1 font-heading text-2xl text-agri-forest tabular-nums">{fmtMoney(fp?.permanent_investment || 0)}</div></div>
          <div><div className="label-cap">Temporary money in business</div><div className="mt-1 font-heading text-2xl text-agri-amber-dark tabular-nums">{fmtMoney(fp?.temporary_in_business || 0)}</div><div className="text-[11px] text-agri-ink-2">still to return to yourself</div></div>
          <div><div className="label-cap">Total capital in</div><div className="mt-1 font-heading text-2xl tabular-nums">{fmtMoney(fp?.total_owner_capital || 0)}</div></div>
        </div>
      </div>

      {/* Balance sheet — how business worth is built */}
      {fp && (
        <div className="card-soft p-6">
          <h3 className="font-heading text-xl mb-3">What the business is worth</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm max-w-2xl">
            <div className="sm:col-span-2 label-cap text-agri-forest">Assets</div>
            <Row label="Cash in hand" value={fp.cash_in_hand} plus />
            <Row label="Stock on hand (at cost)" value={fp.stock_value_cost} plus />
            <Row label="Owed by customers (receivable)" value={fp.receivable} plus />
            {fp.supplier_advances > 0 && <Row label="Advance with suppliers" value={fp.supplier_advances} plus />}
            <div className="sm:col-span-2 label-cap text-agri-danger mt-2">Liabilities</div>
            <Row label="Owed to suppliers (payable)" value={fp.payable} minus />
            {fp.client_advances > 0 && <Row label="Store credit owed to customers" value={fp.client_advances} minus />}
            <div className="sm:col-span-2 border-t border-[rgba(28,28,30,0.1)] mt-1 pt-2 flex justify-between font-bold">
              <span>Business worth</span><span className="tabular-nums text-agri-forest">{fmtMoney(fp.business_worth)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Cash breakdown */}
      {b && (
        <div className="card-soft p-6">
          <h3 className="font-heading text-xl mb-3">How cash in hand is calculated</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm max-w-2xl">
            <Row label="Capital put in" value={b.capital_in} plus />
            <Row label="Cash from sales" value={b.cash_from_sales} plus />
            <Row label="Payments collected from clients" value={b.collections} plus />
            <Row label="Cash paid on purchases" value={b.cash_purchases} minus />
            <Row label="Payments to suppliers" value={b.supplier_paid} minus />
            <Row label="Expenses paid" value={b.expenses_paid} minus />
            <Row label="Withdrawals (cash taken out)" value={b.withdrawals} minus />
            <div className="sm:col-span-2 border-t border-[rgba(28,28,30,0.1)] mt-1 pt-2 flex justify-between font-bold">
              <span>Cash in hand</span><span className="tabular-nums text-agri-forest">{fmtMoney(fp?.cash_in_hand || 0)}</span>
            </div>
          </div>
          {b.expenses_unpaid > 0 && <p className="text-[11px] text-agri-ink-2 mt-3">Note: {fmtMoney(b.expenses_unpaid)} of unpaid expenses are not deducted from cash yet.</p>}
        </div>
      )}

      {/* Daily cash book — cash in hand / credit / net cash, per day */}
      <div className="card-soft">
        <div className="p-5 border-b border-[rgba(28,28,30,0.06)]">
          <h3 className="font-heading text-xl">Daily Cash Book</h3>
          <p className="text-xs text-agri-ink-2 mt-0.5">Per day (each day on its own): cash received, credit given (udhaar), cash paid out, and net cash. Tap a day to see every transaction behind it.</p>
        </div>
        <div className="max-h-[30rem] overflow-y-auto">
          <TableCard className="!shadow-none">
            <thead className={theadCls}>
              <tr>
                <th className={thCls}>Date</th>
                <th className={thNumCls}>Cash in Hand</th>
                <th className={thNumCls}>Credit (udhaar)</th>
                <th className={thNumCls}>Cash Out</th>
                <th className={thNumCls}>Net Cash</th>
              </tr>
            </thead>
            <tbody>
              {daily.length === 0 && <EmptyRow colSpan={5} icon={Wallet}>No cash activity yet.</EmptyRow>}
              {daily.map((d) => (
                <tr key={d.date} className={`${rowCls} cursor-pointer hover:bg-agri-bg/60`} onClick={() => setDetailDate(d.date)} title="View this day's transactions">
                  <td data-label="Date" className={`${tdCls} whitespace-nowrap font-medium text-agri-forest underline decoration-dotted underline-offset-2`}>{new Date(d.date).toLocaleDateString()}</td>
                  <td data-label="Cash in Hand" className={`${tdNumCls} font-semibold text-agri-forest`}>{fmtMoney(d.cash_received)}</td>
                  <td data-label="Credit (udhaar)" className={`${tdNumCls} text-agri-amber-dark`}>{fmtMoney(d.credit)}</td>
                  <td data-label="Cash Out" className={`${tdNumCls} text-agri-danger`}>{fmtMoney(d.cash_out)}</td>
                  <td data-label="Net Cash" className={`${tdNumCls} font-bold ${d.net_cash >= 0 ? "text-agri-forest" : "text-agri-danger"}`}>{fmtMoney(d.net_cash)}</td>
                </tr>
              ))}
              {daily.length > 0 && (
                <tr className="bg-agri-bg/50 border-t-2 border-[rgba(28,28,30,0.12)] font-bold">
                  <td className={`${tdCls} whitespace-nowrap`}>Total · {daily.length} day{daily.length === 1 ? "" : "s"}</td>
                  <td className={`${tdNumCls} text-agri-forest`}>{fmtMoney(daily.reduce((s, d) => s + d.cash_received, 0))}</td>
                  <td className={`${tdNumCls} text-agri-amber-dark`}>{fmtMoney(daily.reduce((s, d) => s + d.credit, 0))}</td>
                  <td className={`${tdNumCls} text-agri-danger`}>{fmtMoney(daily.reduce((s, d) => s + d.cash_out, 0))}</td>
                  <td className={tdNumCls}>{fmtMoney(daily.reduce((s, d) => s + d.net_cash, 0))}</td>
                </tr>
              )}
            </tbody>
          </TableCard>
        </div>
      </div>

      {/* Day detail — every transaction behind a day's row */}
      <Modal open={!!detailDate} onClose={() => setDetailDate(null)} title={detailDate ? `Cash Book · ${new Date(detailDate).toLocaleDateString()}` : ""} size="lg">
        {detailLoading || !detail ? (
          <div className="text-agri-ink-2 text-sm">Loading…</div>
        ) : (
          <div className="space-y-4">
            <DetailSection title="Cash In (sales + collections)" tone="forest" rows={detail.cash_in} total={detail.totals.cash_in} />
            {detail.capital_in.length > 0 && <DetailSection title="Capital In (investment / top-up)" tone="forest" rows={detail.capital_in} total={detail.totals.capital_in} />}
            {detail.credit_given.length > 0 && <DetailSection title="Credit Given (udhaar)" tone="amber" rows={detail.credit_given} total={detail.totals.credit} />}
            <DetailSection title="Cash Out (purchases, suppliers, expenses, withdrawals)" tone="danger" rows={detail.cash_out} total={detail.totals.cash_out} />
            <div className="border-t border-[rgba(28,28,30,0.12)] pt-3 space-y-1.5">
              <div className="flex justify-between text-sm font-bold">
                <span>Net Cash (cash in − cash out)</span>
                <span className={`tabular-nums ${detail.totals.net_cash >= 0 ? "text-agri-forest" : "text-agri-danger"}`}>{fmtMoney(detail.totals.net_cash)}</span>
              </div>
              {detail.totals.capital_in > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-agri-ink-2">Net incl. capital in</span>
                  <span className={`tabular-nums font-semibold ${detail.totals.net_incl_capital >= 0 ? "text-agri-forest" : "text-agri-danger"}`}>{fmtMoney(detail.totals.net_incl_capital)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Ledger */}
      <div className="card-soft">
        <div className="p-5 border-b border-[rgba(28,28,30,0.06)]">
          <h3 className="font-heading text-xl">Capital & Cash ledger</h3>
          <p className="text-xs text-agri-ink-2 mt-0.5">Money you put in (investment / temporary top-up) and take out (withdrawal).</p>
        </div>
        <TableCard className="!shadow-none">
          <thead className={theadCls}>
            <tr><th className={thCls}>Date</th><th className={thCls}>Type</th><th className={thCls}>Note</th><th className={thNumCls}>Amount</th><th className={thCls}></th></tr>
          </thead>
          <tbody>
            {moves.length === 0 && <EmptyRow colSpan={5} icon={Wallet}>No entries yet.</EmptyRow>}
            {moves.map((m) => {
              const t = TYPES[m.type] || { label: m.type, tone: "", sign: "" };
              return (
                <tr key={m.id} className={rowCls}>
                  <td data-label="Date" className={`${tdCls} whitespace-nowrap text-agri-ink-2`}>{new Date(m.date || m.created_at).toLocaleDateString()}</td>
                  <td data-label="Type" className={tdCls}><span className={`text-xs font-medium ${t.tone}`}>{t.label}</span></td>
                  <td data-label="Note" className={tdCls}>{m.note}</td>
                  <td data-label="Amount" className={`${tdNumCls} font-semibold ${t.tone}`}>{t.sign} {fmtMoney(m.amount)}</td>
                  <td className={`${tdCls} text-right rt-actions`}><button onClick={() => window.confirm("Delete this entry?") && del.mutate(m.id)} className="text-agri-ink-2 hover:text-agri-danger"><Trash2 size={15} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </TableCard>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Cash / Capital">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Type</label>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(TYPES).map(([k, t]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, type: k })}
                  className={`text-sm font-medium px-3 py-2 rounded-lg border text-left transition-colors ${form.type === k ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-agri-ink-2 mt-1">
              {form.type === "investment" && "Permanent capital you invest in the business."}
              {form.type === "topup" && "Temporary cash added for a purchase — you'll take it back after sales."}
              {form.type === "withdrawal" && "Cash you take out (repay a temporary top-up, or draw profit)."}
            </p>
          </div>
          <div>
            <label className={labelCls}>Amount</label>
            <NumberInput required value={form.amount} onChange={(n) => { setForm({ ...form, amount: n }); fe.clearField("amount"); }} className={fe.fieldError("amount") ? inputErrCls : ""} />
            <FieldError>{fe.fieldError("amount")}</FieldError>
          </div>
          <div><label className={labelCls}>Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Note</label><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Starting investment, Temporary for Changshang order" className={inputCls} /></div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setOpen(false)} className={btnOutline}>Cancel</button><button className={btnPrimary} disabled={save.isPending}>Save</button></div>
        </form>
      </Modal>
    </div>
  );
}

function Row({ label, value, plus, minus }) {
  return (
    <div className="flex justify-between">
      <span className="text-agri-ink-2">{label}</span>
      <span className={`tabular-nums ${minus ? "text-agri-danger" : plus ? "text-agri-forest" : ""}`}>{minus ? "−" : plus ? "+" : ""} {fmtMoney(value || 0)}</span>
    </div>
  );
}

function DetailSection({ title, tone, rows, total }) {
  const toneCls = tone === "danger" ? "text-agri-danger" : tone === "amber" ? "text-agri-amber-dark" : "text-agri-forest";
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <div className="label-cap">{title}</div>
        <div className={`text-sm font-bold tabular-nums ${toneCls}`}>{fmtMoney(total)}</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-agri-ink-2">None.</div>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="flex justify-between items-center text-sm py-1.5 border-b border-[rgba(28,28,30,0.05)]">
              <div className="min-w-0 pr-3">
                <span className="text-agri-ink">{r.name || "—"}</span>
                <span className="text-xs text-agri-ink-2 ml-1.5">{r.kind}{r.ref ? ` · ${r.ref}` : ""}</span>
              </div>
              <div className={`tabular-nums whitespace-nowrap ${toneCls}`}>{fmtMoney(r.amount)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
